import axios from 'axios';

import { API_BASE_URL } from '../config';

// API_BASE_URL is now imported from config

// Configure axios defaults
axios.defaults.withCredentials = true;

// Configure default headers
const axiosConfig = {
  headers: {
    'Content-Type': 'application/json',
    'Accept': '*/*'
  }
};

export interface LoginRequest {
  username?: string;
  mobileNumber: string;
  password?: string;
  remember: boolean;
  isOTP: boolean;
}

export interface SendOTPResponse {
  isSuccess: boolean;
  message: string;
  data: {
    isUserExist: boolean;
  };
}

export interface VerifyOTPRequest {
  mobileNumber: string;
  otp: string;
  remember: boolean;
  isOTP: boolean;
}

export interface VerifyOTPResponse {
  isSuccess: boolean;
  message: string;
  data: {
    isUserExist: boolean;
  };
}

export interface RegisterAndLoginRequest {
  username: string;
  mobileNumber: string;
  password: string;
  remember: boolean;
  isOTP: boolean;
}

export interface AuthResponse {
  token: string;
  refreshToken?: string;
  expiresIn: number;
  expiration?: string;
}

const TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const MOBILE_NUMBER_KEY = 'user_mobile_number';
const USERNAME_KEY = 'user_username';

// Setup axios interceptors immediately
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Clear tokens on 401 error
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      // Redirect to login page
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

export const httpService = {
  sendOTP: async (data: LoginRequest): Promise<SendOTPResponse> => {
    try {
      const response = await axios.post(`${API_BASE_URL}/Login/SendOTP`, data, axiosConfig);
      return response.data;
    } catch (error) {
      console.error('Send OTP Error:', error);
      throw error;
    }
  },

  verifyOTP: async (data: VerifyOTPRequest): Promise<VerifyOTPResponse> => {
    try {
      console.log('🔍 Verifying OTP with data:', data);
      const response = await axios.post(`${API_BASE_URL}/Login/RegisterAndLogin`, data, axiosConfig);
      console.log('✅ OTP verification response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ OTP verification error:', error);
      throw error;
    }
  },

  getTrip: async (tripId: string): Promise<any> => {
    try {
      console.log('🔍 Getting trip details, tripId:', tripId);
      const response = await axios.get(`${API_BASE_URL}/Trip/${tripId}`, axiosConfig);
      console.log('✅ Get trip response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Get trip error:', error);
      throw error;
    }
  },

          registerAndLogin: async (data: RegisterAndLoginRequest): Promise<AuthResponse> => {
          try {
            console.log('📤 Calling RegisterAndLogin API with data:', data);
            const response = await axios.post(`${API_BASE_URL}/Login/RegisterAndLogin`, data, axiosConfig);
            
            console.log('📥 Raw API response:', response);
            console.log('📦 Full response.data:', response.data);
            console.log('🔍 Response data type:', typeof response.data);
            console.log('🔍 Response data keys:', Object.keys(response.data));
            
            // Extract token from the nested data structure
            const token = response.data.data?.token;
            const expiration = response.data.data?.expiration;
            
            console.log('🔍 Extracted data:', {
              token: token ? `✅ Token found (${token.substring(0, 20)}...)` : '❌ No token',
              expiration: expiration || '❌ No expiration',
              hasData: !!response.data.data,
              dataKeys: response.data.data ? Object.keys(response.data.data) : []
            });
            
            const authData: AuthResponse = {
              token: token || '',
              refreshToken: '', // API doesn't return refresh token
              expiresIn: 0, // We'll calculate this from expiration if needed
              expiration: expiration || ''
            };

            console.log('🔐 Parsed auth data:', {
              token: authData.token ? `✅ Token (${authData.token.substring(0, 20)}...)` : '❌ No token',
              expiration: authData.expiration || '❌ No expiration',
              hasToken: !!authData.token,
              tokenLength: authData.token?.length
            });

            // Save tokens and user info to localStorage
            localStorage.setItem(TOKEN_KEY, authData.token);
            localStorage.setItem(MOBILE_NUMBER_KEY, data.mobileNumber);
            localStorage.setItem(USERNAME_KEY, data.username);
            if (authData.refreshToken) {
              localStorage.setItem(REFRESH_TOKEN_KEY, authData.refreshToken);
            }

            console.log('🔐 Token and user info saved successfully:', {
              token: authData.token ? `✅ Token saved (${authData.token.substring(0, 20)}...)` : '❌ No token',
              mobileNumber: data.mobileNumber,
              username: data.username,
              expiration: authData.expiration || '❌ No expiration'
            });

            return authData;
          } catch (error) {
            console.error('❌ Register and Login Error:', error);
            throw error;
          }
        },

  getToken: (): string | null => {
    const token = localStorage.getItem(TOKEN_KEY);
    console.log('🔑 Current token:', token ? `✅ ${token.substring(0, 20)}...` : '❌ No token found');
    return token;
  },

  getMobileNumber: (): string | null => {
    const mobileNumber = localStorage.getItem(MOBILE_NUMBER_KEY);
    console.log('📱 Current mobile number:', mobileNumber || '❌ No mobile number found');
    return mobileNumber;
  },

  getUsername: (): string | null => {
    const username = localStorage.getItem(USERNAME_KEY);
    console.log('👤 Current username:', username || '❌ No username found');
    return username;
  },

  getUserInfo: () => {
    const mobileNumber = localStorage.getItem(MOBILE_NUMBER_KEY);
    const username = localStorage.getItem(USERNAME_KEY);
    const token = localStorage.getItem(TOKEN_KEY);
    
    console.log('👤 Current user info:', {
      mobileNumber: mobileNumber || '❌ No mobile number',
      username: username || '❌ No username',
      hasToken: !!token
    });
    
    return {
      mobileNumber,
      username,
      hasToken: !!token
    };
  },

  getRefreshToken: (): string | null => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    console.log('🔄 Current refresh token:', refreshToken ? `✅ ${refreshToken.substring(0, 20)}...` : '❌ No refresh token found');
    return refreshToken;
  },

  isAuthenticated: (): boolean => {
    const token = localStorage.getItem(TOKEN_KEY);
    const isAuth = !!token;
    console.log('🔒 Authentication status:', isAuth ? '✅ Authenticated' : '❌ Not authenticated');
    return isAuth;
  },

  logout: (): void => {
    // Clear all authentication tokens
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    
    // Clear user information
    localStorage.removeItem(MOBILE_NUMBER_KEY);
    localStorage.removeItem(USERNAME_KEY);
    
    // Clear any other user-related data
    localStorage.removeItem('user_profile');
    localStorage.removeItem('user_preferences');
    
    // Clear session storage if used
    sessionStorage.clear();
    
    // Clear axios default headers
    delete axios.defaults.headers.common['Authorization'];
    
    console.log('🚪 User logged out, all data cleared');
    
    // Optional: Redirect to login page
    // Note: This should be handled by the component calling logout
    // to avoid circular dependencies
  },

  // Generic POST method for B2C endpoints with authentication
  postB2C: async (endpoint: string, data: any) => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) {
        console.error('❌ No authentication token found for POST request to:', endpoint);
        throw new Error('No authentication token found');
      }

      console.log(`📤 Making authenticated POST request to: ${endpoint}`);
      console.log('🔑 Using token:', `${token.substring(0, 20)}...`);
      console.log('📦 Request data:', data);

      const config = {
        ...axiosConfig,
        headers: {
          ...axiosConfig.headers,
          'Authorization': `Bearer ${token}`
        }
      };

      const response = await axios.post(`${API_BASE_URL}/${endpoint}`, data, config);
      console.log(`✅ POST ${endpoint} successful:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`❌ POST ${endpoint} Error:`, error);
      throw error;
    }
  },

  // Generic GET method for B2C endpoints with authentication
  getB2C: async (endpoint: string) => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) {
        console.error('❌ No authentication token found for GET request to:', endpoint);
        throw new Error('No authentication token found');
      }

      console.log(`📤 Making authenticated GET request to: ${endpoint}`);
      console.log('🔑 Using token:', `${token.substring(0, 20)}...`);

      const config = {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': '*/*'
        }
      };

      const response = await axios.get(`${API_BASE_URL}/${endpoint}`, config);
      console.log(`✅ GET ${endpoint} successful:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`❌ GET ${endpoint} Error:`, error);
      throw error;
    }
  },

  // Generic PUT method for B2C endpoints with authentication
  putB2C: async (endpoint: string, data: any) => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) {
        console.error('❌ No authentication token found for PUT request to:', endpoint);
        throw new Error('No authentication token found');
      }

      console.log(`📤 Making authenticated PUT request to: ${endpoint}`);
      console.log('🔑 Using token:', `${token.substring(0, 20)}...`);
      console.log('📦 Request data:', data);

      const config = {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': '*/*'
        }
      };

      const response = await axios.put(`${API_BASE_URL}/${endpoint}`, data, config);
      console.log(`✅ PUT ${endpoint} successful:`, response.data);
      return response.data;
    } catch (error: any) {
      console.error(`❌ PUT ${endpoint} Error:`, error);
      console.error(`❌ Error details:`, {
        message: error.message,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data,
        config: error.config
      });
      throw error;
    }
  },

  // Create trip with authentication
  createTrip: async (data: any) => {
    console.log('🚗 Creating trip with authentication...');
    console.log('🔑 Current token status:', httpService.isAuthenticated() ? '✅ Token available' : '❌ No token');
    return await httpService.postB2C('CreateTrip', data);
  },

  // Get user profile
  getUserProfile: async () => {
    return await httpService.getB2C('User/Profile');
  },

  // Get trip history
  getTripHistory: async () => {
    return await httpService.getB2C('Trip/History');
  },

  // Get trip list with pagination
  getTripList: async (pageNumber: number = 1, pageSize: number = 10) => {
    console.log('🚗 Getting trip list with pagination...');
    const data = {
      pageNumber,
      pageSize
    };
    return await httpService.postB2C('TripList', data);
  },

  // Get trip options
  getTripOptions: async (tripId: string) => {
    console.log('🚗 Getting trip options for trip ID:', tripId);
    return await httpService.getB2C(`TripOptions?tripId=${tripId}`);
  },

  // Get trip details
  getTripDetails: async (tripId: string) => {
    console.log('📋 Getting trip details for trip ID:', tripId);
    // Using specific trip ID for testing
    return await httpService.getB2C(`Trip/${tripId}`);
  },

  // Set trip option
  setTripOption: async (data: any) => {
    console.log('🚗 Setting trip option...');
    return await httpService.putB2C('SetTripOption', data);
  },

  // Update user profile
  updateUserProfile: async (data: any) => {
    return await httpService.postB2C('User/UpdateProfile', data);
  },

  // Rate trip
  rateTrip: async (tripId: string, rate: number, comment: string) => {
    console.log('⭐ Rating trip:', { tripId, rate, comment });
    const data = {
      tripId,
      rate,
      comment
    };
    return await httpService.postB2C('RateTrip', data);
  },

  // Update trip comment (for chat data)
  updateTripComment: async (tripId: string, comment: string) => {
    console.log('💬 Updating trip comment:', { tripId, comment: comment.substring(0, 100) + '...' });
    const data = {
      tripId,
      comment
    };
    return await httpService.putB2C('Trip/UpdateComment', data);
  },

  // Add chat message
  addChatMessage: async (data: {
    tripId: string;
    content: string;
    type: 'text' | 'file' | 'image' | 'system';
    senderType: number;
    attachments: Array<{ fileName: string; fileUrl: string }>;
  }) => {
    console.log('💬 Adding chat message:', { tripId: data.tripId, content: data.content.substring(0, 50) + '...' });
    return await httpService.postB2C('AddChatMessage', data);
  },

  // Get chat messages
  getChatMessages: async (tripId: string, pageNumber: number = 1, pageSize: number = 10) => {
    console.log('📥 Getting chat messages:', { tripId, pageNumber, pageSize });
    const data = {
      tripId,
      pageNumber,
      pageSize
    };
    return await httpService.postB2C('GetChatMessages', data);
  }
}; 